import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { getCatalogSummary } from '../api/supplierCatalog'
import { listSupplierInbox } from '../api/supplierInbox'
import { useAuth } from '../auth/AuthProvider'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { StatTile, StatTileRow } from '../components/StatTile'

export const SupplierOverviewScreen = () => {
  const { session } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [catalogTotal, setCatalogTotal] = useState(0)
  const [needsAttention, setNeedsAttention] = useState(0)
  const [pendingInbox, setPendingInbox] = useState(0)
  const [pendingRequests, setPendingRequests] = useState<
    { rfqId: number; title: string; restaurant: string }[]
  >([])

  useEffect(() => {
    if (!session) {
      return
    }
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const [summary, inbox] = await Promise.all([
          getCatalogSummary(session.organizationId),
          listSupplierInbox(session.organizationId, 0, 20),
        ])
        const pending = inbox.items.filter((item) => item.decisionStatus === 'pending')
        setCatalogTotal(summary.totalItems)
        setNeedsAttention(summary.needsAttention)
        setPendingInbox(pending.length)
        setPendingRequests(
          pending.slice(0, 5).map((item) => ({
            rfqId: item.rfqId,
            title: item.rfqTitle,
            restaurant: item.restaurantOrganizationName,
          })),
        )
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить сводку')
      } finally {
        setLoading(false)
      }
    })()
  }, [session])

  const pendingLink =
    pendingRequests.length === 1
      ? `/orders?rfqId=${pendingRequests[0].rfqId}`
      : '/orders'

  const hasPriorityWork = pendingInbox > 0 || needsAttention > 0

  return (
    <section className="market-page market-page--overview">
      <MarketPageHeader
        title="Обзор"
        description="Что требует внимания: входящие запросы и каталог."
        actions={
          pendingInbox > 0 ? (
            <Link to={pendingLink} className="echat-btn echat-btn--primary echat-btn--sm">
              Входящие запросы
            </Link>
          ) : needsAttention > 0 ? (
            <Link to="/catalog" className="echat-btn echat-btn--primary echat-btn--sm">
              Разобрать каталог
            </Link>
          ) : (
            <Link to="/catalog/import" className="echat-btn echat-btn--primary echat-btn--sm">
              Импорт прайса
            </Link>
          )
        }
      />

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !error ? (
        <>
          {hasPriorityWork ? (
            <section className="market-overview-priority" aria-label="Требует внимания">
              <div className="market-overview-priority__inner">
                <div className="market-overview-priority__content">
                  <h2 className="market-overview-priority__title">
                    {pendingInbox > 0 ? 'Новые запросы' : 'Каталог требует внимания'}
                  </h2>
                  {pendingInbox > 0 ? (
                    <p className="market-overview-priority__text">
                      {pendingInbox === 1
                        ? 'Один запрос от ресторана ждёт вашего решения.'
                        : `${pendingInbox} запроса от ресторанов ждут вашего решения.`}
                    </p>
                  ) : null}
                  {needsAttention > 0 ? (
                    <p className="market-overview-priority__text">
                      {needsAttention === 1
                        ? '1 позиция в каталоге без категории — уточните сопоставление.'
                        : `${needsAttention} поз. в каталоге без категории — уточните сопоставление.`}
                    </p>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          <StatTileRow className="market-overview-metrics">
            <StatTile label="Позиций в каталоге" value={catalogTotal} />
            <StatTile label="Без категории" value={needsAttention} emphasis={needsAttention > 0} />
            <StatTile label="Новых запросов" value={pendingInbox} emphasis={pendingInbox > 0} />
          </StatTileRow>

          {pendingRequests.length > 0 ? (
            <article className="echat-panel">
              <h2 className="echat-panel__subtitle">Ожидают решения</h2>
              <ul className="market-overview-list">
                {pendingRequests.map((item) => (
                  <li key={item.rfqId}>
                    <Link to={`/orders?rfqId=${item.rfqId}`} className="market-overview-link">
                      {item.title}
                    </Link>
                    <p className="echat-muted">{item.restaurant}</p>
                  </li>
                ))}
              </ul>
            </article>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
